import React from "react";
import { Bot, RotateCcw, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

import "./GptChat.css";

export default function GptResponseCard({
  title,
  content = "",
  conversation = [],
  showDisclaimer = false,
  disclaimer,
  primaryAction,
  secondaryAction,
  followUpPlaceholder,
  followUpLabel,
  loadingLabel,
  flowId,
  stepId,
  onContinue,
}) {
  const { t } = useTranslation("assistantAi");
  const labels = t("gptChat.response", { returnObjects: true });
  const [followUp, setFollowUp] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const finalTitle = title || labels.title;
  const finalDisclaimer = disclaimer || labels.disclaimer;
  const finalPlaceholder = followUpPlaceholder || labels.placeholder;
  const finalFollowUpLabel = followUpLabel || labels.submit;
  const finalLoadingLabel = loadingLabel || labels.loading;
  const displayContent =
    showDisclaimer && finalDisclaimer
      ? String(content || "").replace(String(finalDisclaimer).trim(), "").trim()
      : content;
  const messages = Array.isArray(conversation) && conversation.length
    ? conversation
    : [{ role: "assistant", content: displayContent || content }];
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, submitting]);

  const trigger = (action) => {
    onContinue?.({ flowId, stepId }, action?.payload || {});
  };

  const submitFollowUp = async () => {
    const message = followUp.trim();
    if (!message || submitting) return;
    setSubmitting(true);
    try {
      await onContinue?.({ flowId, stepId }, { message });
      setFollowUp("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="guided-gpt-card guided-gpt-card--response">
      <div className="guided-gpt-card__header">
        <div className="guided-gpt-card__title">
          <span className="guided-gpt-card__icon" aria-hidden="true">
            <Bot size={16} />
          </span>
          <strong>{finalTitle}</strong>
        </div>
        <div className="guided-gpt-card__toolbar">
          {primaryAction && (
            <button
              type="button"
              className="guided-gpt-icon-button guided-gpt-icon-button--primary"
              onClick={() => trigger(primaryAction)}
              title={primaryAction.label || labels.reset}
              aria-label={primaryAction.label || labels.reset}
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="guided-gpt-thread" ref={scrollRef}>
        {messages.map((message, index) => (
          <div
            className={`guided-gpt-thread__message ${
              message.role === "user" ? "guided-gpt-thread__message--user" : "guided-gpt-thread__message--assistant"
            }`}
            key={`${message.role || "message"}-${index}`}
          >
            {message.content}
          </div>
        ))}
        {submitting && (
          <div className="guided-gpt-thread__message guided-gpt-thread__message--assistant guided-gpt-thread__message--loading">
            <div className="guided-gpt-loading__dots">
              <span />
              <span />
              <span />
            </div>
            <p>{finalLoadingLabel}</p>
          </div>
        )}
      </div>

      {showDisclaimer && finalDisclaimer && (
        <div className="guided-gpt-disclaimer">{finalDisclaimer}</div>
      )}

      <div className="guided-gpt-followup">
        <textarea
          value={followUp}
          placeholder={finalPlaceholder}
          disabled={submitting}
          rows={3}
          onChange={(event) => setFollowUp(event.target.value)}
        />
        <button
          type="button"
          className="guided-gpt-submit"
          onClick={submitFollowUp}
          disabled={!followUp.trim() || submitting}
        >
          <Send size={14} />
          <span>{submitting ? finalLoadingLabel : finalFollowUpLabel}</span>
        </button>
      </div>

      {secondaryAction && (
        <div className="guided-gpt-card__actions">
          <button type="button" className="guided-gpt-secondary" onClick={() => trigger(secondaryAction)}>
            {secondaryAction.label || labels.continue}
          </button>
        </div>
      )}
    </div>
  );
}
